<!-- src/views/Dashboard/DashboardMain.vue -->
<template>
  <div class="container mx-auto pb-10">
    <!-- 도움말 버튼 -->
    <div class="flex justify-end mb-2">
      <button
        id="help-button"
        @click="startTour"
        class="flex items-center gap-1.5 mt-1 px-3 py-1.5 text-[13px] text-gray-800 border border-gray-400 hover:bg-gray-50 rounded-md transition-all"
        title="도움말 보기"
      >
        <i class="mdi mdi-help-circle-outline text-base"></i>
        가이드
      </button>
    </div>

    <div class="flex-1 min-w-[600px]">

      <!-- ========== 1) 프로젝트 정보 테이블 ========== -->
      <div id="tour-project-info" class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <!-- 헤더: 클릭 시 '툴바 위의 모든 요소' 접기/펼치기 -->
        <div
          class="flex items-center justify-between px-5 py-3 bg-slate-800 cursor-pointer select-none"
          @click="toggleAboveToolbar"
          :aria-expanded="isAboveToolbarOpen"
        >
          <div class="flex items-center gap-2.5">
            <i class="mdi mdi-folder-outline text-white/80 text-base"></i>
            <span class="text-[13px] font-semibold text-white tracking-wide">프로젝트 정보</span>
          </div>
          <i
            :class="isAboveToolbarOpen ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"
            class="text-white/60 text-lg transition-transform duration-200"
          ></i>
        </div>

        <!-- 컬럼 헤더 + 데이터 행 -->
        <div v-show="isAboveToolbarOpen">
          <div class="flex px-5 py-2 bg-gray-50 border-b border-gray-200">
            <div v-for="col in columns" :key="col.value" class="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider" :style="{ width: col.width }">
              {{ col.label }}
            </div>
          </div>
          <div class="overflow-y-auto max-h-[140px]">
            <div
              v-for="(row, index) in rowsProject"
              :key="index"
              class="flex items-center px-5 py-2.5 text-[13px] border-b border-gray-100 last:border-0 transition-colors hover:bg-violet-50/50"
              :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'"
            >
              <div
                v-for="col in columns"
                :key="col.value"
                :class="[col.value === 'project_name' ? 'text-left font-medium text-gray-800' : 'text-center text-gray-500']"
                :style="{ width: col.width }"
                :title="row[col.value]"
              >
                {{ row[col.value] }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== 2) 크롤링 리스트 + 툴바 + 결과 ========== -->
      <div class="flex flex-wrap justify-between gap-6 max-w-[1400px] mx-auto">
        <div class="flex-1 min-w-[600px]">

          <!-- (툴바 위 그룹) 크롤링 리스트 헤더/바디: 프로젝트 헤더로 함께 접힘 -->
          <template v-if="isAboveToolbarOpen">
            <div class="mt-2 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <!-- 리스트 헤더: 내부 행만 토글 -->
              <div
                id="tour-crawling-list"
                class="flex items-center justify-between px-5 py-3 bg-slate-800 cursor-pointer select-none"
                @click="toggleCrawlingSection"
                :aria-expanded="isCrawlingOpen"
              >
                <div class="flex items-center gap-2.5">
                  <i class="mdi mdi-database-outline text-white/80 text-base"></i>
                  <span class="text-[13px] font-semibold text-white tracking-wide">크롤링 리스트</span>
                  <span v-if="selectedCrawlingItems.length" class="ml-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-violet-500 text-white">
                    {{ selectedCrawlingItems.length }}
                  </span>
                </div>
                <i
                  :class="isCrawlingOpen ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"
                  class="text-white/60 text-lg transition-transform duration-200"
                ></i>
              </div>

              <!-- 리스트 바디 -->
              <div class="overflow-y-auto max-h-[180px]" v-show="isCrawlingOpen">
                <div
                  v-for="(row, index) in rowsCrawling"
                  :key="row.job_id ?? index"
                  class="flex items-center gap-3 px-5 py-2.5 text-[13px] border-b border-gray-100 last:border-0 cursor-pointer transition-colors"
                  :class="[
                    row.selected
                      ? 'bg-violet-50/70 hover:bg-violet-100/60'
                      : index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/40 hover:bg-gray-100/50'
                  ]"
                  @click="handleCrawlingRowClick(row)"
                >
                  <div class="flex-none flex items-center justify-center">
                    <input
                      type="checkbox"
                      v-model="row.selected"
                      @click.stop
                      class="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500/30 cursor-pointer"
                    />
                  </div>
                  <div class="flex-1 text-left truncate" :class="row.selected ? 'text-gray-800 font-medium' : 'text-gray-600'" :title="row.job_name">
                    {{ row.job_name }}
                  </div>
                </div>
              </div>

              <div v-if="!rowsCrawling.length && isCrawlingOpen" class="flex items-center justify-center gap-2 py-6 text-gray-400 text-sm">
                <i class="mdi mdi-information-outline"></i>
                데이터가 없습니다.
              </div>
            </div>
          </template>

          <!-- ===== 툴바 (항상 보임) ===== -->
          <div id="tour-toolbar" class="mt-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div class="flex items-end flex-wrap gap-5 relative">

              <!-- 기간 설정 -->
              <div class="flex items-end gap-2">
                <div class="flex flex-col gap-1.5">
                  <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">시작일자</label>
                  <input
                    type="date"
                    class="px-3 py-2 h-[38px] border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50/50
                           focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400
                           hover:border-gray-300 transition-all relative z-10"
                    v-model="dateFrom"
                    @click.stop
                  >
                </div>
                <span class="text-gray-300 pb-2 text-lg">~</span>
                <div class="flex flex-col gap-1.5">
                  <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">종료일자</label>
                  <input
                    type="date"
                    class="px-3 py-2 h-[38px] border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50/50
                           focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400
                           hover:border-gray-300 transition-all relative"
                    v-model="dateTo"
                    @click.stop
                  >
                </div>
              </div>

              <!-- 구분선 -->
              <div class="hidden sm:block w-px h-[38px] bg-gray-200"></div>

              <!-- 채널 선택 멀티셀렉트 -->
              <div class="flex flex-col gap-1.5" ref="multiRoot" @keydown.escape="isOpenMulti=false">
                <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">채널</label>
                <div class="relative w-[300px]">
                  <button
                    type="button"
                    class="relative w-full h-[38px] border rounded-lg bg-gray-50/50 px-3 pr-8
                          flex items-center gap-1.5 text-sm text-left
                          transition-all duration-150
                          hover:border-gray-300 hover:bg-white
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-200"
                    :class="[ isOpenMulti ? 'border-violet-400 ring-2 ring-violet-200 bg-white' : 'border-gray-200' ]"
                    @mousedown.stop
                    @click.stop="isOpenMulti = !isOpenMulti"
                  >
                    <template v-if="selectedChannels.length">
                      <div class="flex items-center gap-1 overflow-x-auto whitespace-nowrap">
                        <span
                          v-for="ch in selectedChannelObjects"
                          :key="ch.value"
                          class="inline-flex items-center gap-1 bg-violet-500 text-white rounded-md px-2 py-[2px] text-[11px] font-medium"
                          @mousedown.stop
                          @click.stop
                        >
                          {{ ch.label }}
                          <span class="cursor-pointer opacity-80 hover:opacity-100 ml-0.5" @mousedown.stop @click.stop="removeChannel(ch.value)">
                            <i class="mdi mdi-close text-[10px]"></i>
                          </span>
                        </span>
                      </div>
                    </template>
                    <span v-else class="text-gray-400">채널을 선택하세요</span>
                    <span class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true">
                      <i class="mdi mdi-chevron-down text-lg"></i>
                    </span>
                  </button>

                  <div
                    v-show="isOpenMulti"
                    class="absolute mt-1.5 w-full bg-white border border-gray-200 rounded-xl shadow-lg shadow-gray-200/50 z-[100] overflow-hidden"
                  >
                    <ul class="max-h-60 overflow-auto py-1.5 text-sm">
                      <li
                        v-for="ch in channels"
                        :key="ch.value"
                        class="px-3 py-2 flex items-center gap-2.5 cursor-pointer transition-colors"
                        :class="selectedChannels.includes(ch.value) ? 'bg-violet-50/70 text-violet-700' : 'hover:bg-gray-50 text-gray-700'"
                        @mousedown.stop
                        @click.stop="handleToggleChannel(ch.value)"
                      >
                        <input
                          type="checkbox"
                          class="w-3.5 h-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500/30 pointer-events-none"
                          :checked="selectedChannels.includes(ch.value)"
                          @click.prevent
                        />
                        <span class="text-[13px]">{{ ch.label }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- 구분선 -->
              <div class="hidden sm:block w-px h-[38px] bg-gray-200"></div>

              <!-- 키워드 인풋 -->
              <div class="flex flex-col gap-1.5">
                <label class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">키워드</label>
                <div class="relative">
                  <i class="mdi mdi-magnify absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                  <input
                    type="text"
                    class="pl-9 pr-3 py-2 h-[38px] w-[200px] border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50/50
                           placeholder:text-gray-400
                           focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400
                           hover:border-gray-300 transition-all"
                    v-model.trim="keyword"
                    placeholder="키워드 입력"
                    @click.stop
                  />
                </div>
              </div>

            </div>
          </div>

          <!-- ===== 탭 메뉴 ===== -->
          <div id="tour-tabs" class="mt-5">
            <nav class="inline-flex bg-gray-100 rounded-xl p-1 gap-1" aria-label="Tabs">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="switchTab(tab.id)"
                :class="[
                  'relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200/60'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                ]"
              >
                <i :class="[tab.mdi, 'text-base', activeTab === tab.id ? 'text-violet-500' : 'text-gray-400']"></i>
                {{ tab.label }}
              </button>
            </nav>
          </div>

          <div v-if="vizStale && activeTab === 'visualization'" class="mt-3 flex items-center gap-2.5 px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 text-amber-700 text-sm">
            <i class="mdi mdi-alert-circle-outline text-lg text-amber-500 flex-none"></i>
            조건이 변경되었습니다. 다시 <b class="ml-0.5">시각화</b> 버튼을 클릭해 주세요.
          </div>

          <!-- <div v-if="errors.length" class="mt-3 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
            <ul class="list-disc pl-5">
              <li v-for="(msg, i) in errors" :key="i">{{ msg }}</li>
            </ul>
          </div> -->

          <!-- ========== 탭 컨텐츠 영역 ========== -->
          <div id="tour-tab-content" class="mt-4">
            <!-- 시각화 탭 -->
            <div v-if="activeTab === 'visualization'">
              <!-- 데이터 로딩 중 -->
              <div v-if="dataLoading">
                <div class="bg-white rounded-md border border-gray-200 shadow-sm p-20 flex items-center justify-center min-h-[400px]">
                  <div class="text-center">
                    <div class="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-violet-600"></div>
                    <p class="mt-4 text-base font-medium text-gray-700">데이터를 불러오는 중...</p>
                  </div>
                </div>
              </div>

              <!-- 시각화 결과 -->
              <div v-else-if="showViz && vizPayload" class="relative">
                <VizPanel
                  :line-labels="vizPayload.lineLabels"
                  :line-datasets="vizPayload.lineDatasets"
                  :line2-labels="vizPayload.line2Labels"
                  :line2-datasets="vizPayload.line2Datasets"
                  :channel-table="vizPayload.channelTable"
                  :counts-table="vizPayload.countsTable"
                  :wc-labels="vizPayload.wcLabels"
                  :wc-weights="vizPayload.wcWeights"
                  :keyword-table="vizPayload.keywordTable"
                  :height="320"
                  @charts-ready="onChartsReady"
                />

                <!-- 차트 렌더링 중 오버레이 -->
                <div v-if="chartsLoading" class="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center z-20 rounded-md">
                  <div class="text-center">
                    <div class="inline-block animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-violet-600"></div>
                    <p class="mt-4 text-base font-medium text-gray-700">차트를 그리는 중...</p>
                  </div>
                </div>
              </div>

              <!-- 조건 안내 및 시각화 버튼 -->
              <div v-else class="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 via-white to-violet-50/30 shadow-sm p-10">
                <div class="max-w-sm mx-auto text-center">
                  <div class="mx-auto w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mb-5">
                    <i class="mdi mdi-chart-line text-2xl text-violet-500"></i>
                  </div>
                  <h3 class="text-base font-semibold text-gray-800">시각화 조건 설정</h3>
                  <p class="mt-1.5 text-sm text-gray-400">아래 조건을 모두 충족하면 시각화를 실행할 수 있습니다.</p>

                  <div class="mt-6 space-y-2.5 text-left">
                    <div
                      v-for="cond in vizConditions"
                      :key="cond.key"
                      class="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors"
                      :class="cond.met ? 'bg-emerald-50/80' : 'bg-gray-50'"
                    >
                      <span
                        class="flex-none w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors"
                        :class="cond.met ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'"
                      >
                        <i :class="cond.met ? 'mdi mdi-check' : 'mdi mdi-minus'"></i>
                      </span>
                      <span class="text-sm" :class="cond.met ? 'text-emerald-700 font-medium' : 'text-gray-500'">{{ cond.label }}</span>
                    </div>
                  </div>

                  <button
                    v-if="!visualizeDisabled"
                    @click="onVisualize"
                    class="mt-7 inline-flex items-center gap-2 px-7 py-2.5 rounded-lg text-white text-sm font-medium bg-slate-800 hover:bg-slate-900 shadow-md shadow-slate-200/50 transition-all active:scale-[0.98]"
                  >
                    <i class="mdi mdi-play-circle-outline text-lg"></i>
                    시각화 실행
                  </button>
                </div>
              </div>
            </div>

            <!-- 추천프롬프트 탭 -->
            <div v-else-if="activeTab === 'recommendPrompt'">
              <div class="rounded-xl border border-gray-100 bg-gradient-to-br from-amber-50/40 via-white to-orange-50/30 shadow-sm p-10">
                <div class="max-w-sm mx-auto text-center">
                  <div class="mx-auto w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-5">
                    <i class="mdi mdi-lightbulb-on-outline text-2xl text-amber-500"></i>
                  </div>
                  <h3 class="text-base font-semibold text-gray-800">추천프롬프트 조건 설정</h3>
                  <p class="mt-1.5 text-sm text-gray-400">아래 조건을 모두 충족하면 추천프롬프트를 생성할 수 있습니다.</p>

                  <div class="mt-6 space-y-2.5 text-left">
                    <div
                      v-for="cond in vizConditions"
                      :key="cond.key"
                      class="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors"
                      :class="cond.met ? 'bg-emerald-50/80' : 'bg-gray-50'"
                    >
                      <span
                        class="flex-none w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors"
                        :class="cond.met ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'"
                      >
                        <i :class="cond.met ? 'mdi mdi-check' : 'mdi mdi-minus'"></i>
                      </span>
                      <span class="text-sm" :class="cond.met ? 'text-emerald-700 font-medium' : 'text-gray-500'">{{ cond.label }}</span>
                    </div>
                  </div>

                  <button
                    v-if="!visualizeDisabled"
                    @click="onRecommendPromptPage"
                    class="mt-7 inline-flex items-center gap-2 px-7 py-2.5 rounded-lg text-white text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-200/50 transition-all active:scale-[0.98]"
                  >
                    <i class="mdi mdi-arrow-right-circle-outline text-lg"></i>
                    추천프롬프트 페이지로 이동
                  </button>
                </div>
              </div>
            </div>

            <!-- 보고서생성 탭 -->
            <div v-else-if="activeTab === 'reportGenerate'">
              <!-- 조건 충족 시 ReportPanel 표시 -->
              <div v-if="!visualizeDisabled">
                <ReportPanel
                  :report-data="reportData"
                  :project-id="currentProjectId"
                  :job-ids="selectedJobIds"
                  :start-date="dateFrom"
                  :end-date="dateTo"
                  :channels="selectedChannels"
                  @close="activeTab = 'visualization'"
                  @generate-success="onReportGenerateSuccess"
                  @generate-error="onReportGenerateError"
                />
              </div>
              <!-- 조건 미충족 시 안내 -->
              <div v-else class="rounded-xl border border-gray-100 bg-gradient-to-br from-slate-50 via-white to-violet-50/30 shadow-sm p-10">
                <div class="max-w-sm mx-auto text-center">
                  <div class="mx-auto w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mb-5">
                    <i class="mdi mdi-file-document-outline text-2xl text-violet-500"></i>
                  </div>
                  <h3 class="text-base font-semibold text-gray-800">보고서 생성 조건 설정</h3>
                  <p class="mt-1.5 text-sm text-gray-400">아래 조건을 모두 충족하면 보고서를 생성할 수 있습니다.</p>

                  <div class="mt-6 space-y-2.5 text-left">
                    <div
                      v-for="cond in vizConditions"
                      :key="cond.key"
                      class="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors"
                      :class="cond.met ? 'bg-emerald-50/80' : 'bg-gray-50'"
                    >
                      <span
                        class="flex-none w-6 h-6 rounded-full flex items-center justify-center text-xs transition-colors"
                        :class="cond.met ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'"
                      >
                        <i :class="cond.met ? 'mdi mdi-check' : 'mdi mdi-minus'"></i>
                      </span>
                      <span class="text-sm" :class="cond.met ? 'text-emerald-700 font-medium' : 'text-gray-500'">{{ cond.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 보고서생성내역 탭 -->
            <div v-else-if="activeTab === 'reportHistory'">
              <ReportHistoryPanel :project-id="currentProjectId" />
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter , useRoute } from 'vue-router'
import VizPanel from './VizPanel.vue'
import ReportPanel from './ReportPanel.vue'
import ReportHistoryPanel from './ReportHistoryPanel.vue'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'

const route = useRoute()
const router = useRouter()
const store = useStore()

// ========== 투어 가이드 ==========
const driverObj = ref(null)

function startTour() {
  // 투어 시작 전 섹션들 펼치기
  isAboveToolbarOpen.value = true
  isCrawlingOpen.value = true

  nextTick(() => {
    driverObj.value = driver({
      showProgress: true,
      animate: true,
      allowClose: true,
      overlayClickNext: false,
      stagePadding: 10,
      popoverClass: 'tour-popover',
      nextBtnText: '다음',
      prevBtnText: '이전',
      doneBtnText: '완료',
      progressText: '{{current}} / {{total}}',
      steps: [
        {
          element: '#tour-project-info',
          popover: {
            title: '📋 프로젝트 정보',
            description: '현재 선택된 프로젝트의 기본 정보(프로젝트명, 기간, 현황, 참여 인원)를 확인할 수 있습니다.<br/><br/>헤더를 클릭하면 접기/펼치기가 가능합니다.',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '#tour-crawling-list',
          popover: {
            title: '📊 크롤링 리스트',
            description: '분석할 크롤링 데이터를 선택합니다.<br/><br/><b>체크박스를 클릭</b>하여 원하는 데이터를 선택하세요.<br/>최소 1개 이상 선택해야 분석이 가능합니다.',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '#tour-toolbar',
          popover: {
            title: '🔧 분석 조건 설정',
            description: '데이터 분석에 필요한 조건을 설정합니다.<br/><br/>• <b>시작일/종료일</b>: 분석 기간 설정<br/>• <b>채널 선택</b>: 분석할 채널 선택 (복수 선택 가능)<br/>• <b>키워드</b>: 검색 키워드 입력 (선택사항)',
            side: 'bottom',
            align: 'center'
          }
        },
        {
          element: '#tour-tabs',
          popover: {
            title: '📑 기능 탭',
            description: '원하는 기능을 탭으로 선택합니다.<br/><br/>• <b>시각화</b>: 데이터를 차트로 시각화<br/>• <b>추천프롬프트</b>: AI 추천 프롬프트 생성<br/>• <b>보고서생성</b>: 분석 보고서 작성<br/>• <b>보고서생성내역</b>: 생성된 보고서 목록',
            side: 'bottom',
            align: 'start'
          }
        },
        {
          element: '#tour-tab-content',
          popover: {
            title: '📈 결과 영역',
            description: '선택한 탭의 결과가 이 영역에 표시됩니다.<br/><br/>조건을 모두 설정하면 각 탭에서 <b>실행 버튼</b>이 활성화됩니다.<br/><br/>조건 충족 여부는 ✓/✗ 표시로 확인할 수 있습니다.',
            side: 'top',
            align: 'center'
          }
        },
        {
          element: '#help-button',
          popover: {
            title: '❓ 도움말',
            description: '언제든지 이 버튼을 클릭하면 도움말 투어를 다시 볼 수 있습니다.<br/><br/>이제 대시보드를 사용해보세요! 🎉',
            side: 'bottom',
            align: 'end'
          }
        }
      ]
    })

    driverObj.value.drive()
  })
}

// ✅ '툴바 위 요소' 토글 상태 (프로젝트 행 + 크롤링 헤더/행)
const isAboveToolbarOpen = ref(true)
// 크롤링 리스트 내부(행) 토글
const isCrawlingOpen = ref(true)

function toggleAboveToolbar() {
  isAboveToolbarOpen.value = !isAboveToolbarOpen.value
  // 접힐 때 내부 상태도 닫아두면 UX 깔끔
  if (!isAboveToolbarOpen.value) isCrawlingOpen.value = false
}
function toggleCrawlingSection() {
  isCrawlingOpen.value = !isCrawlingOpen.value
}

const rowsProject = ref([]) 
const rowsCrawling = ref([]) 
const uniq = arr => [...new Set(arr)]

onMounted(() => {
  const project_id = route.params?.project_id
  handleSearch(project_id)
})

async function handleSearch(project_id) {
  await store.dispatch('dashboard/getProjectInfoList', project_id)  
  await store.dispatch('dashboard/getDashboardCrawlingList', project_id) 

  rowsProject.value = store.getters['dashboard/projectInfoListList']
  rowsCrawling.value = store.getters['dashboard/dashboardCrawlingList']
}

/* 프로젝트 테이블 */
const columns = [
  { label: '프로젝트명', value: 'project_name', width: '45%' },
  { label: '기간',      value: 'period',        width: '20%' },
  { label: '현황',      value: 'status',        width: '15%' }, 
  { label: '참여 인원', value: 'members',       width: '20%' }
]

function handleCrawlingRowClick(row) { row.selected = !row.selected }

/* 날짜 기본값 */
function formatLocalDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const _today = new Date()
const _from  = new Date(_today)
_from.setMonth(_from.getMonth() - 3)
const dateFrom = ref(formatLocalDate(_from))
const dateTo   = ref(formatLocalDate(_today))

/* 멀티셀렉트 */
const channels = ref([
  { label: '언론',   value: '언론' },
  { label: '블로그', value: '블로그' }, 
  { label: '지식인', value: '지식인' }, 
  { label: '커뮤니티', value: '커뮤니티' }, 
  { label: '카페', value: '카페' }, 
  { label: '유튜브', value: '유튜브' }, 
  { label: '정부기관', value: '정부기관' }, 
  { label: '페이스북', value: '페이스북' }, 
  { label: '인스타그램', value: '인스타그램' }, 
  { label: '카카오스토리', value: '카카오스토리' }, 
  { label: '트위터', value: '트위터' },  
])
 
const selectedChannels = ref([])
const keyword = ref('') 
const isOpenMulti = ref(false)
const multiRoot = ref(null)
const selectedChannelObjects = computed(() =>
  channels.value.filter(ch => selectedChannels.value.includes(ch.value))
)
function handleToggleChannel(val) {
  const i = selectedChannels.value.indexOf(val)
  if (i >= 0) selectedChannels.value.splice(i, 1)
  else selectedChannels.value.push(val)
}
function removeChannel(val) {
  const i = selectedChannels.value.indexOf(val)
  if (i >= 0) selectedChannels.value.splice(i, 1)
}

/* ✅ 바깥 클릭 시 드롭다운 닫기 (pointerdown + capture) */
function onClickOutside(e) {
  const root = multiRoot.value
  if (!root) return
  if (isOpenMulti.value && !root.contains(e.target)) {
    isOpenMulti.value = false
  }
}
onMounted(() => document.addEventListener('pointerdown', onClickOutside, true))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onClickOutside, true))

/* 검증/상태 */
const errors  = ref([])
const showViz = ref(false)
const vizStale = ref(false)
const vizPayload = ref(null)
const dataLoading = ref(false)
const chartsLoading = ref(false)
const chartsLoadingStartTime = ref(0)

/* 보고서 상태 */
const reportData = ref(null)

/* 탭 상태 */
const activeTab = ref('visualization')
const tabs = [
  { id: 'visualization', label: '시각화', mdi: 'mdi mdi-chart-line' },
  { id: 'recommendPrompt', label: '추천프롬프트', mdi: 'mdi mdi-lightbulb-on-outline' },
  { id: 'reportGenerate', label: '보고서생성', mdi: 'mdi mdi-file-document-outline' },
  { id: 'reportHistory', label: '보고서생성내역', mdi: 'mdi mdi-history' }
]

function switchTab(tabId) {
  activeTab.value = tabId

  // 탭 전환 시 필요한 초기화 또는 액션
  if (tabId === 'reportGenerate') {
    // 보고서 생성 탭 - 검증 후 reportData 설정
    if (validateForVisualization()) {
      reportData.value = {
        crawlingItems: selectedCrawlingItems.value.map(r => r.job_name),
        startDate: dateFrom.value,
        endDate: dateTo.value,
        channels: [...selectedChannels.value],
        keyword: keyword.value,
        createdAt: new Date().toLocaleString('ko-KR')
      }
    }
  }
}

const selectedCrawlingItems = computed(() => rowsCrawling.value.filter(r => r.selected))
const selectedJobIds = computed(() => selectedCrawlingItems.value.map(r => r.id).filter(Boolean))
const currentProjectId = computed(() => {
  const id = route.params?.project_id
  return id ? Number(id) : null
})
const hasValidDates = computed(() => dateFrom.value && dateTo.value && dateTo.value >= dateFrom.value)
const hasChannels = computed(() => selectedChannels.value.length > 0)
const visualizeDisabled = computed(() =>
  selectedCrawlingItems.value.length < 1 || !hasValidDates.value || !hasChannels.value
)
const vizConditions = computed(() => [
  { key: 'crawl', label: '크롤링 데이터 선택', met: selectedCrawlingItems.value.length > 0 },
  { key: 'dates', label: '시작일 / 종료일 설정', met: hasValidDates.value },
  { key: 'channel', label: '채널 선택', met: hasChannels.value }
])
function validateForVisualization() {
  errors.value = []
  if (selectedCrawlingItems.value.length < 1) errors.value.push('크롤링 리스트에서 최소 1개 이상 선택하세요.')
  if (!dateFrom.value || !dateTo.value)       errors.value.push('시작일자와 종료일자를 모두 선택하세요.')
  else if (dateTo.value < dateFrom.value)     errors.value.push('종료일은 시작일보다 작을 수 없습니다.')
  if (selectedChannels.value.length < 1)      errors.value.push('채널을 최소 1개 이상 선택하세요.')
  return errors.value.length === 0
}

/* 시각화 버튼 */
async function onVisualize() {
  if (!validateForVisualization()) {
    showViz.value = false;
    vizStale.value = false;
    return;
  }

  // 즉시 로딩 상태 표시
  dataLoading.value = true
  showViz.value = false
  errors.value = []

  try {
    /* 1) 라인 차트용 호출 */
    const cvsPaths = uniq(
      selectedCrawlingItems.value
        .map(r => r.channel_counts_path)
        .filter(Boolean)
    )
    if (!cvsPaths.length) {
      errors.value = ['선택한 항목에 유효한 channel_counts_path가 없습니다.']
      showViz.value = false
      vizStale.value = true
      return
    }

    const payload = {
      cvs_paths: cvsPaths,
      channel_list: [...selectedChannels.value],
      start_date: dateFrom.value,
      end_date: dateTo.value,
      keyword: keyword.value,
      vizPanel_type: '1'
    }
    await store.dispatch('dashboard/postVizPanel', payload)
    
    const vizPanelData = store.getters['dashboard/vizPanelData']
    if (!vizPanelData?.chartData?.labels || !vizPanelData?.chartData?.datasets) {
      throw new Error('시각화에 필요한 chartData가 없습니다.')
    } 

    const labels = vizPanelData.chartData.labels
    const lineDatasets = vizPanelData.chartData.datasets.map(ds => ([
      ds.label,
      Array.isArray(ds.data) ? ds.data : []
    ])).map(([label, data]) => ({ label, data }))

    const channelTable = lineDatasets
      .map(ds => ({
        label: ds.label,
        count: ds.data.reduce((a, b) => a + (Number(b) || 0), 0)
      }))
      .sort((a, b) => b.count - a.count)

    // 1차 페이로드
    vizPayload.value = {
      lineLabels: labels,
      lineDatasets,
      line2Labels: [],
      line2Datasets: [],
      channelTable,
      countsTable: [],
      wcLabels: [],
      wcWeights: [],
      keywordTable: []
    }

    /* 2) 워드클라우드용 호출 */
    const cvsWordPaths = uniq(
      selectedCrawlingItems.value
        .map(r => r.wordcloud_path)
        .filter(Boolean)
    )
    if (!cvsWordPaths.length) {
      errors.value = ['선택한 항목에 유효한 wordcloud_path가 없습니다.']
      showViz.value = false
      vizStale.value = true
      return
    }

    const payload_word = {
      cvs_paths: cvsWordPaths,
      channel_list: [...selectedChannels.value],
      start_date: dateFrom.value,
      end_date: dateTo.value,
      keyword: keyword.value,
      vizPanel_type: '2'
    }
    await store.dispatch('dashboard/postVizPanel', payload_word)
    const vizPanelWordData = store.getters['dashboard/vizPanelData']
    
    const wordsArr = Array.isArray(vizPanelWordData?.chartData?.words)
      ? vizPanelWordData.chartData.words
      : []

    const wcLabels = wordsArr.map(w => w.word)
    const wcWeights = wordsArr.map(w => Number(w.count) || 0)

    const keywordTable = wordsArr
      .slice(0, 120)
      .map(w => ({ keyword: String(w.word ?? ''), count: Number(w.count)||0 }))

    vizPayload.value.wcLabels = wcLabels
    vizPayload.value.wcWeights = wcWeights
    vizPayload.value.keywordTable = keywordTable

    /* 3) 개수용 호출 - 2번째 라인차트 & countsTable 갱신 */
    const cvsountsPaths = uniq(
      selectedCrawlingItems.value
        .map(r => r.keyword_counts_path)
        .filter(Boolean)
    )
    if (!cvsountsPaths.length) {
      errors.value = ['선택한 항목에 유효한 keyword_counts_path가 없습니다.']
      showViz.value = false
      vizStale.value = true
      return
    }
     
    const payload_counts = {
      cvs_paths: cvsountsPaths,
      channel_list: [...selectedChannels.value],
      start_date: dateFrom.value,
      end_date: dateTo.value,
      keyword: keyword.value,
      vizPanel_type: '3'
    }
    await store.dispatch('dashboard/postVizPanel', payload_counts)
    const vizPanelCountData = store.getters['dashboard/vizPanelData']

    const cLabels = vizPanelCountData?.chartData?.labels ?? []
    const cDatasets = Array.isArray(vizPanelCountData?.chartData?.datasets)
      ? vizPanelCountData.chartData.datasets.map(ds => ({
          label: String(ds.label ?? ''),
          data: Array.isArray(ds.data) ? ds.data.map(v => Number(v) || 0) : []
        })).filter(ds => ds.label)
      : []

    vizPayload.value.line2Labels = cLabels
    vizPayload.value.line2Datasets = cDatasets

    const countsTable = cDatasets
      .map(ds => ({
        label: ds.label,
        count: ds.data.reduce((a, b) => a + (Number(b) || 0), 0)
      }))
      .sort((a, b) => b.count - a.count)
    vizPayload.value.countsTable = countsTable

    // 데이터 로딩 완료, 차트 렌더링 시작
    dataLoading.value = false
    showViz.value = true
    chartsLoading.value = true
    chartsLoadingStartTime.value = Date.now()
    vizStale.value = false
    errors.value = []

    await nextTick()
  } catch (err) {
    console.error('onVisualize error:', err)
    errors.value = [err?.message || '시각화 데이터 요청에 실패했습니다.']
    dataLoading.value = false
    showViz.value = false
    vizStale.value = true
    vizPayload.value = null
    chartsLoading.value = false
  }
}

async function onChartsReady() {
  // 최소 500ms 동안 로딩 상태 유지
  const MIN_LOADING_TIME = 500
  const elapsed = Date.now() - chartsLoadingStartTime.value
  const remaining = Math.max(0, MIN_LOADING_TIME - elapsed)

  if (remaining > 0) {
    await new Promise(resolve => setTimeout(resolve, remaining))
  }

  chartsLoading.value = false
}

/* 필터 변경 시 결과 숨김 */
function invalidateViz() {
  errors.value = []
  if (showViz.value || dataLoading.value) {
    showViz.value = false
    vizStale.value = true
    vizPayload.value = null
    dataLoading.value = false
    chartsLoading.value = false
    chartsLoadingStartTime.value = 0
  }
}

watch([dateFrom, dateTo], invalidateViz)
watch(selectedChannels, invalidateViz, { deep: true })
watch(rowsCrawling, invalidateViz, { deep: true })
watch(keyword, invalidateViz)

/* 보고서 생성 이벤트 핸들러 */
function onReportGenerateSuccess(result) {
  console.log('보고서 생성 완료:', result)
}

function onReportGenerateError(error) {
  console.error('보고서 생성 실패:', error)
  errors.value = [error.message || '보고서 생성에 실패했습니다.']
}


// script setup 안에 추가
function onRecommendPromptPage() {
  if (!validateForVisualization()) return

  const params = {
    crawlingItems: selectedCrawlingItems.value.map(r => r.job_name),
    startDate: dateFrom.value,
    endDate: dateTo.value,
    channels: [...selectedChannels.value],
    keyword: keyword.value
  }

  router.push({
    name: 'PromptRecommendPage',
    query: params
  })
}


</script>

<style scoped>
.sr-only {
  position:absolute; width:1px; height:1px; padding:0; margin:-1px;
  overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0;
}
</style>

<!-- 투어 가이드 전역 스타일 -->
<style>
.driver-popover {
  background-color: #ffffff !important;
  border-radius: 12px !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15) !important;
  max-width: 360px !important;
}

.driver-popover-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
  padding-bottom: 8px !important;
}

.driver-popover-description {
  font-size: 14px !important;
  color: #475569 !important;
  line-height: 1.6 !important;
}

.driver-popover-progress-text {
  font-size: 12px !important;
  color: #94a3b8 !important;
}

.driver-popover-navigation-btns {
  gap: 8px !important;
}

.driver-popover-prev-btn,
.driver-popover-next-btn {
  padding: 8px 16px !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.2s !important;
}

.driver-popover-prev-btn {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border: none !important;
}

.driver-popover-prev-btn:hover {
  background-color: #e2e8f0 !important;
}

.driver-popover-next-btn {
  background-color: #7c3aed !important;
  color: white !important;
  border: none !important;
}

.driver-popover-next-btn:hover {
  background-color: #6d28d9 !important;
}

.driver-popover-close-btn {
  color: #94a3b8 !important;
}

.driver-popover-close-btn:hover {
  color: #64748b !important;
}

.driver-popover-arrow-side-left.driver-popover-arrow,
.driver-popover-arrow-side-right.driver-popover-arrow,
.driver-popover-arrow-side-top.driver-popover-arrow,
.driver-popover-arrow-side-bottom.driver-popover-arrow {
  border-color: #ffffff !important;
}
</style>
